/** @jsxRuntime classic */
/** @jsx h */

import {
  Component,
  JSX,
  Accessor,
  createMemo,
  createContext,
  useContext,
  type Context,
} from 'solid-js'
import solidH from 'solid-js/h'
import * as $ from './constants'
import * as parse from './parse'
import { MarkdownToJSX, RuleType, RequireAtLeastOne } from './types'
import * as util from './utils'

export { parser } from './parse'
import { parser } from './parse'

export { RuleType, type MarkdownToJSX } from './types'
export { sanitizer, slugify } from './utils'


const hasDOM = typeof document !== 'undefined'

type SolidASTRender = (
  ast: MarkdownToJSX.ASTNode | MarkdownToJSX.ASTNode[],
  state: Omit<MarkdownToJSX.State, 'key'>
) => JSX.Element | JSX.Element[]

type HTag = string | Component<Record<string, unknown>>
type HProps = Record<string, unknown>
type HChildren = JSX.Element | JSX.Element[] | string | null

// Normalize children to array format
const toArray = <T,>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value]

// Check if value is a SolidJS component
const isComponent = (
  value: unknown
): value is Component<Record<string, unknown>> =>
  typeof value === 'function' ||
  (typeof value === 'object' && value !== null && 'render' in value)

function render(
  node: MarkdownToJSX.ASTNode,
  output: SolidASTRender,
  state: Omit<MarkdownToJSX.State, 'key'>,
  h: (tag: HTag, props: HProps, ...children: HChildren[]) => JSX.Element,
  sanitize: (value: string, tag: string, attribute: string) => string | null,
  slug: (input: string, defaultFn: (input: string) => string) => string,
  refs: { [key: string]: { target: string; title: string } },
  options: SolidOptions
): JSX.Element | string | null {
  switch (node.type) {
    case RuleType.blockQuote: {
      const props = {} as Record<string, unknown>
      let children = node.children
      if (node.alert) {
        props.class =
          'markdown-alert-' + slug(node.alert.toLowerCase(), util.slugify)
        children = [util.alertHeaderNode(node.alert), ...children]
      }
      return h('blockquote', props, ...toArray(output(children, state)))
    }

    case RuleType.breakLine:
      return h('br', {})

    case RuleType.breakThematic:
      return h('hr', {})

    case RuleType.frontmatter:
      if (options.preserveFrontmatter) {
        return h('pre', {}, node.text)
      }
      return null

    case RuleType.codeBlock: {
      const decodedLang = node.lang
        ? util.decodeEntityReferences(node.lang)
        : ''
      const codeProps = {
        ...(node.attrs || {}),
        class: decodedLang
          ? `language-${decodedLang} lang-${decodedLang}`
          : '',
      } as Record<string, unknown>
      return h('pre', {}, h('code', codeProps, node.text))
    }

    case RuleType.codeInline:
      return h('code', {}, node.text)

    case RuleType.footnoteReference:
      return h(
        'a',
        { href: sanitize(node.target, 'a', 'href') },
        h('sup', {}, node.text)
      )

    case RuleType.gfmTask:
      return h('input', {
        checked: node.completed,
        readOnly: true,
        type: 'checkbox',
      })

    case RuleType.heading:
      return h(
        `h${node.level}`,
        { id: node.id },
        ...toArray(output(node.children, state))
      )

    case RuleType.htmlBlock: {
      const htmlNode = node as MarkdownToJSX.HTMLNode

      // Apply options.tagfilter: escape dangerous tags
      if (options.tagfilter && util.shouldFilterTag(htmlNode.tag)) {
        const tagText =
          typeof htmlNode._rawText === 'string'
            ? htmlNode._rawText
            : `<${htmlNode.tag}${util.formatFilteredTagAttrs(htmlNode.attrs)}>`
        return h('span', {}, tagText)
      }

      if (htmlNode._rawText && htmlNode._verbatim) {
        // Type 1 blocks (script, style, pre, textarea) always render verbatim
        const tagLower = (htmlNode.tag as string).toLowerCase()
        const isType1Block = parse.isType1Block(tagLower)

        if (isType1Block) {
          const textContent = util.type1TextContent(
            htmlNode._rawText,
            tagLower,
            options.tagfilter
          )
          if (/<[a-z][^>]{0,100}>/i.test(htmlNode._rawText)) {
            return h(node.tag, {
              ...node.attrs,
              innerHTML: textContent,
            })
          }
          return h(node.tag, { ...node.attrs }, textContent)
        }

        const containsPreTags = /<\/?pre\b/i.test(htmlNode._rawText)
        if (containsPreTags) {
          const innerHtml = options.tagfilter
            ? util.applyTagFilterToText(htmlNode._rawText)
            : htmlNode._rawText
          return h(node.tag, {
            ...node.attrs,
            innerHTML: innerHtml,
          })
        }
        // Re-parse rawText so a nested element does not absorb a following text
        // line, then split at this element's own closing tag so any trailing
        // content renders as siblings (issue #881).
        const parseOptions: parse.ParseOptions = {
          slugify: (input: string) => slug(input, util.slugify),
          sanitizer: sanitize,
          tagfilter: true,
        }
        const cleanedText = htmlNode._rawText
          .replace(/>\s+</g, '><')
          .replace(/\n+/g, ' ')
          .trim()

        const selfTagRegex = new RegExp(
          `^<${htmlNode.tag}(\\s[^>]*)?>(\\s*</${htmlNode.tag}>)?$`,
          'i'
        )
        if (selfTagRegex.test(cleanedText)) {
          return h(node.tag, { ...node.attrs })
        }

        const astNodes = parse.parseMarkdown(
          cleanedText,
          { inline: false, refs: refs, inHTML: false },
          parseOptions
        )
        util.stripVerbatim(astNodes)

        // rawText already contains its own wrapper element, so emit the parsed
        // nodes directly instead of nesting them in another copy of the tag.
        if (util.isFullVerbatimBlock(cleanedText, htmlNode.tag as string)) {
          return output(astNodes.flatMap(util.processVerbatimNode), state)
        }

        const split = util.findOwnCloseInAST(astNodes, tagLower)
        if (split.found && split.afterClose.length > 0) {
          return [
            h(node.tag, { ...node.attrs }, ...toArray(output(split.beforeClose.flatMap(util.processVerbatimNode), state))),
            ...toArray(output(split.afterClose.flatMap(util.processVerbatimNode), state)),
          ]
        }

        const processedChildren = output(astNodes.flatMap(util.processVerbatimNode), state)
        return h(node.tag, { ...node.attrs }, ...toArray(processedChildren))
      }
      if (util.isVoidElement(node.tag)) {
        return h(node.tag, { ...node.attrs })
      }
      return h(
        node.tag,
        { ...node.attrs },
        ...(node.children ? toArray(output(node.children, state)) : [])
      )
    }

    case RuleType.htmlSelfClosing: {
      const htmlNode = node as MarkdownToJSX.HTMLSelfClosingNode

      // Apply options.tagfilter: escape dangerous self-closing tags
      if (options.tagfilter && util.shouldFilterTag(htmlNode.tag)) {
        const tagText =
          typeof htmlNode._rawText === 'string'
            ? htmlNode._rawText
            : `<${htmlNode.tag}${util.formatFilteredTagAttrs(htmlNode.attrs)} />`
        return h('span', {}, tagText)
      }

      return h(htmlNode.tag, { ...htmlNode.attrs })
    }

    case RuleType.image: {
      return h('img', {
        alt: node.alt && node.alt.length > 0 ? node.alt : undefined,
        title: node.title || undefined,
        src: sanitize(node.target, 'img', 'src'),
      } as Record<string, unknown>)
    }

    case RuleType.link: {
      const props: Record<string, unknown> = {}
      if (node.target != null) props.href = util.encodeUrlTarget(node.target)
      if (node.title) props.title = node.title
      return h('a', props, ...toArray(output(node.children, state)))
    }

    case RuleType.table: {
      const table = node as MarkdownToJSX.TableNode
      return h(
        'table',
        {},
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            ...table.header.map(function generateHeaderCell(content, i) {
              return h(
                'th',
                {
                  style:
                    table.align[i] == null ? {} : { textAlign: table.align[i] },
                },
                ...toArray(output(content, state))
              )
            })
          )
        ),
        table.cells.length > 0
          ? h(
              'tbody',
              {},
              ...table.cells.map(function generateTableRow(row, i) {
                return h(
                  'tr',
                  {},
                  ...row.map(function generateTableCell(content, c) {
                    return h(
                      'td',
                      {
                        style:
                          table.align[c] == null
                            ? {}
                            : { textAlign: table.align[c] },
                      },
                      ...toArray(output(content, state))
                    )
                  })
                )
              })
            )
          : null
      )
    }

    case RuleType.text:
      return node.text

    case RuleType.textFormatted:
      return h(
        node.tag as MarkdownToJSX.HTMLTags,
        {},
        ...toArray(output(node.children, state))
      )

    case RuleType.orderedList:
    case RuleType.unorderedList: {
      const Tag = node.type === RuleType.orderedList ? 'ol' : 'ul'

      return h(
        Tag,
        {
          start: node.type === RuleType.orderedList ? node.start : undefined,
        },
        ...node.items.map(function generateListItem(item, i) {
          return h('li', {}, ...toArray(output(item, state)))
        })
      )
    }

    case RuleType.paragraph:
      return h('p', {}, ...toArray(output(node.children, state)))

    case RuleType.ref:
      // Reference definitions should not be rendered (they're consumed during parsing)
      return null

    default:
      return null
  }
}

const createRenderer = (
  userRender: SolidOptions['renderRule'],
  h: (
    tag: HTag,
    props: HProps & {
      class?: string
      id?: string
    },
    ...children: HChildren[]
  ) => JSX.Element,
  sanitize: (value: string, tag: string, attribute: string) => string | null,
  slug: (input: string, defaultFn: (input: string) => string) => string,
  refs: { [key: string]: { target: string; title: string } },
  options: SolidOptions
) => {
  const renderRule = (
    node: MarkdownToJSX.ASTNode,
    renderChildren: (
      children: MarkdownToJSX.ASTNode[]
    ) => JSX.Element | JSX.Element[],
    state: Omit<MarkdownToJSX.State, 'key'>
  ) => {
    const defaultRender = () =>
      render(
        node,
        ast => renderChildren(toArray(ast)),
        state,
        h,
        sanitize,
        slug,
        refs,
        options
      )
    return userRender
      ? userRender(defaultRender, node, renderChildren, state)
      : defaultRender()
  }
  const handleStackOverflow = (ast: MarkdownToJSX.ASTNode[]) =>
    ast.map(node => ('text' in node ? node.text : ''))
  const renderer = (
    ast: MarkdownToJSX.ASTNode[],
    state: Omit<MarkdownToJSX.State, 'key'> = {}
  ) => {
    const depth = (state.renderDepth || 0) + 1
    if (depth > 2500) return handleStackOverflow(ast)

    const result: (JSX.Element | string)[] = []
    let lastWasString = false
    for (let i = 0; i < ast.length; i++) {
      const nodeOut = renderRule(ast[i], renderer, {
          ...state,
          renderDepth: depth,
        }),
        isString = typeof nodeOut === 'string'
      if (isString && lastWasString) {
        // Concatenate consecutive strings
        result[result.length - 1] += nodeOut
      } else if (nodeOut !== null) {
        if (Array.isArray(nodeOut)) {
          // Use loop instead of spread for better performance
          for (let j = 0; j < nodeOut.length; j++) {
            result.push(nodeOut[j])
          }
        } else {
          result.push(nodeOut)
        }
      }
      lastWasString = isString
    }
    return result
  }
  return renderer
}

const getTag = (
  tag: string,
  overrides: SolidOverrides | undefined
): string | Component<Record<string, unknown>> => {
  const override = util.get(overrides, tag, undefined)
  if (!override) return tag
  if (isComponent(override)) return override
  const component = util.get(overrides, `${tag}.component`, tag)
  return isComponent(component) ? component : (component as string)
}

/**
 * Override configuration for HTML tags or custom components in SolidJS output
 */
export type SolidOverride =
  | RequireAtLeastOne<{
      component: string | Component<Record<string, unknown>>
      props: Record<string, unknown>
    }>
  | string
  | Component<Record<string, unknown>>

/**
 * Map of HTML tags and custom components to their override configurations
 */
export type SolidOverrides = {
  [tag in MarkdownToJSX.HTMLTags]?: SolidOverride
} & {
  [customComponent: string]: SolidOverride
}

/**
 * SolidJS compiler options
 */
export type SolidOptions = Omit<
  MarkdownToJSX.Options,
  'createElement' | 'wrapperProps' | 'renderRule' | 'overrides'
> & {
  /** Custom createElement function for SolidJS */
  createElement?: (
    tag: HTag,
    props: HProps,
    ...children: HChildren[]
  ) => JSX.Element
  /** Props for wrapper element */
  wrapperProps?: JSX.HTMLAttributes<HTMLElement>
  /** Custom rendering function for AST rules */
  renderRule?: (
    next: () => JSX.Element | string | null,
    node: MarkdownToJSX.ASTNode,
    renderChildren: (
      children: MarkdownToJSX.ASTNode[]
    ) => JSX.Element | JSX.Element[],
    state: Omit<MarkdownToJSX.State, 'key'>
  ) => JSX.Element | string | null
  /** Override configurations for HTML tags */
  overrides?: SolidOverrides
}

/**
 * Convert AST nodes to SolidJS JSX elements
 *
 * @param ast - Array of AST nodes to render
 * @param options - SolidJS compiler options
 * @returns SolidJS JSX element(s)
 */
export function astToJSX(
  ast: MarkdownToJSX.ASTNode[],
  options?: SolidOptions
): JSX.Element | JSX.Element[] | null {
  const opts = { ...(options || {}) }
  opts.overrides = opts.overrides || {}

  const slug = opts.slugify || util.slugify
  const sanitize = opts.sanitizer || util.sanitizer

  // Recursive compile function for HTML content
  const compileHTML = (input: string) =>
    compiler(input, { ...opts, wrapper: null })

  // JSX helper function - this is what @jsx pragma uses
  function h(
    tag: MarkdownToJSX.HTMLTags | string,
    props: Record<string, unknown> & {
      class?: string
      id?: string
      innerHTML?: string
    },
    ...children: HChildren[]
  ): JSX.Element {
    const overrideProps = util.get(
      opts.overrides,
      `${tag}.props`,
      {}
    ) as Record<string, unknown>

    // Compile any HTML content in props
    const jsxProps = (props || {}) as Record<string, unknown>
    // Only check props that might contain HTML (avoid iterating all props)
    for (const key in jsxProps) {
      const value = jsxProps[key]
      if (
        typeof value === 'string' &&
        value.length > 0 &&
        value[0] === '<' &&
        (parse.HTML_BLOCK_ELEMENT_START_R_ATTR.test(value) ||
          parse.UPPERCASE_TAG_R.test(value) ||
          parse.parseHTMLTag(value, 0))
      ) {
        const compiled = compileHTML(value.trim())
        // For innerHTML, take first element if array (matches original parser behavior)
        jsxProps[key] = key === 'innerHTML' && Array.isArray(compiled)
          ? compiled[0]
          : compiled
      }
    }

    const finalTag = getTag(tag, opts.overrides)
    const mergedClassName =
      util.cx(
        jsxProps?.class as string | undefined,
        (overrideProps.class as string | undefined) || undefined
      ) || undefined

    // Build finalProps efficiently
    const finalProps: Record<string, unknown> = {
      ...jsxProps,
      ...overrideProps,
    }
    if (mergedClassName) finalProps.class = mergedClassName
    // Handle innerHTML for SolidJS (move from jsxProps to finalProps)
    // Only set innerHTML from jsxProps if user didn't provide it in overrides
    if (jsxProps.innerHTML && overrideProps.innerHTML === undefined) {
      finalProps.innerHTML = jsxProps.innerHTML
      delete jsxProps.innerHTML
    }

    return createSolidElement(finalTag, finalProps, ...children)
  }

  // SolidJS createElement helper - uses createSolidElement
  const createElement =
    opts.createElement ||
    ((tag: HTag, props: HProps, ...children: HChildren[]): JSX.Element =>
      createSolidElement(tag, props || {}, ...children))

  const parseOptions: parse.ParseOptions = {
    slugify: i => slug(i, util.slugify),
    sanitizer: sanitize,
    tagfilter: opts.tagfilter !== false,
    disableAutoLink: opts.disableAutoLink,
    disableParsingRawHTML: opts.disableParsingRawHTML,
    enforceAtxHeadings: opts.enforceAtxHeadings,
    forceBlock: opts.forceBlock,
    forceInline: opts.forceInline,
    preserveFrontmatter: opts.preserveFrontmatter,
  }

  const refs =
    ast[0] && ast[0].type === RuleType.refCollection
      ? (ast[0] as MarkdownToJSX.ReferenceCollectionNode).refs
      : {}

  const emitter = createRenderer(
    opts.renderRule,
    h as (
      tag: HTag,
      props: HProps & {
        class?: string
        id?: string
      },
      ...children: HChildren[]
    ) => JSX.Element,
    sanitize,
    slug,
    refs as { [key: string]: { target: string; title: string } },
    opts
  )

  const arr = emitter(ast, {
    inline: opts.forceInline,
    refs: refs,
  }) as (JSX.Element | string)[]

  const footnoteEntries = util.extractFootnoteEntries(refs)

  if (footnoteEntries.length) {
    arr.push(
      h(
        'footer',
        {},
        ...footnoteEntries.map(function createFootnote(def) {
          // We know identifier starts with '^' from the filter above
          const identifierWithoutCaret = def.identifier.slice(1)
          const footnoteAstNodes = parse.parseMarkdown(
            def.footnote,
            { inline: true, refs: refs },
            parseOptions
          )
          const footnoteContent = emitter(footnoteAstNodes, {
            inline: true,
            refs: refs,
          })
          return h(
            'div',
            {
              id: slug(identifierWithoutCaret, util.slugify),
            },
            identifierWithoutCaret + ': ',
            ...toArray(footnoteContent)
          )
        })
      )
    )
  }

  if (opts.wrapper === null) {
    return arr.length === 0
      ? null
      : ((arr.length === 1 ? arr[0] : arr) as JSX.Element | JSX.Element[])
  }

  const wrapper = opts.wrapper || (opts.forceInline ? 'span' : 'div')
  let jsx: JSX.Element | JSX.Element[]

  if (arr.length > 1 || opts.forceWrapper) {
    jsx = arr as JSX.Element[]
  } else if (arr.length === 1) {
    return arr[0] as JSX.Element
  } else {
    return null
  }

  return createElement(
    wrapper as HTag,
    { ...opts.wrapperProps } as HProps,
    jsx
  ) as JSX.Element
}

/**
 * Compile markdown string to SolidJS JSX elements
 *
 * @param markdown - Markdown string to compile
 * @param options - SolidJS compiler options
 * @returns SolidJS JSX element(s)
 */
export function compiler(
  markdown: string = '',
  options: SolidOptions = {}
): JSX.Element | JSX.Element[] | null {
  const opts = { ...(options || {}) }
  opts.overrides = opts.overrides || {}

  const slug = opts.slugify || util.slugify
  const sanitize = opts.sanitizer || util.sanitizer

  function compile(input: string): JSX.Element | JSX.Element[] | null {
    const inline =
      opts.forceInline ||
      (!opts.forceBlock && !util.SHOULD_RENDER_AS_BLOCK_R.test(input))
    const parseOptions: parse.ParseOptions = {
      slugify: i => slug(i, util.slugify),
      sanitizer: sanitize,
      tagfilter: opts.tagfilter !== false,
      disableAutoLink: opts.disableAutoLink,
      disableParsingRawHTML: opts.disableParsingRawHTML,
      enforceAtxHeadings: opts.enforceAtxHeadings,
      forceBlock: opts.forceBlock,
      forceInline: inline,
      preserveFrontmatter: opts.preserveFrontmatter,
    }

    let processedInput = inline ? input : util.prepareBlockInput(input)

    let astNodes = parse.parseMarkdown(
      inline ? input : processedInput,
      { inline: inline, refs: refs },
      parseOptions
    )

    return astToJSX(astNodes, {
      ...opts,
      forceInline: inline,
    } as SolidOptions)
  }

  if (process.env.NODE_ENV !== 'production') {
    util.validateCompilerArgs(markdown, opts.overrides, 'SolidComponent')
  }

  const refs: { [key: string]: { target: string; title: string | undefined } } =
    {}

  return compile(markdown)
}

/**
 * SolidJS context for sharing compiler options across Markdown components
 */
export const MarkdownContext: Context<SolidOptions | undefined> = createContext<
  SolidOptions | undefined
>(undefined)

// Module-level h function for components that use JSX
// This is used by the @jsx pragma for JSX in MarkdownProvider and Markdown components
function createSolidElement(
  tag: string | Component<Record<string, unknown>>,
  props: Record<string, unknown>,
  ...children: HChildren[]
): JSX.Element {
  if (!hasDOM) {
    // Non-DOM environments (tests/SSR) get a structural representation or component output
    const childValue =
      children.length === 0
        ? undefined
        : children.length === 1
          ? children[0]
          : children
    const propsWithChildren =
      childValue === undefined ? props : { ...props, children: childValue }
    if (typeof tag === 'function') {
      return (tag as Component<Record<string, unknown>>)(
        propsWithChildren as Record<string, unknown>
      )
    }
    return {
      t: tag,
      p: propsWithChildren,
    } as unknown as JSX.Element
  }
  const elementFactory = solidH(
    tag as Component<Record<string, unknown>>,
    props,
    ...children
  )
  return typeof elementFactory === 'function'
    ? (elementFactory as () => JSX.Element)()
    : (elementFactory as JSX.Element)
}

// JSX pragma helper; shares createSolidElement with the compiler internals
function h(
  tag: string | Component<Record<string, unknown>>,
  props: Record<string, unknown>,
  ...children: HChildren[]
): JSX.Element {
  return createSolidElement(tag, props, ...children)
}

/**
 * SolidJS context provider for sharing compiler options across Markdown components
 *
 * @param options - Default compiler options to share
 * @param children - SolidJS children
 */
export const MarkdownProvider: Component<{
  options?: SolidOptions
  children: JSX.Element
}> = props => {
  const providerProps = { value: props.options } as Record<string, unknown>
  return h(
    MarkdownContext.Provider as Component<Record<string, unknown>>,
    providerProps,
    props.children
  )
}

/**
 * A SolidJS component for easy markdown rendering. Feed the markdown content as a direct child
 * and the rest is taken care of automatically. Supports reactive content via signals/accessors.
 *
 * @param children - Markdown string content or signal/accessor
 * @param options - Compiler options
 */
export const Markdown: Component<
  Omit<JSX.HTMLAttributes<HTMLElement>, 'children'> & {
    children?: string | Accessor<string> | null
    options?: SolidOptions
  }
> = props => {
  const contextOptions = useContext(MarkdownContext)
  const mergedOptions = createMemo(() => ({
    ...contextOptions,
    ...props.options,
  }))

  const content = createMemo(() => {
    const children =
      typeof props.children === 'function' ? props.children() : props.children
    return children === null || children === undefined ? '' : children
  })

  const ast = createMemo(() =>
    parser(
      content(),
      mergedOptions() as Partial<MarkdownToJSX.Options> &
        Pick<MarkdownToJSX.Options, 'slugify' | 'sanitizer'>
    )
  )
  const jsx = createMemo(() =>
    astToJSX(ast(), {
      ...mergedOptions(),
      wrapperProps: {
        ...mergedOptions()?.wrapperProps,
        ...Object.fromEntries(
          Object.entries(props).filter(
            ([key]) => key !== 'children' && key !== 'options'
          )
        ),
      } as JSX.HTMLAttributes<HTMLElement>,
    })
  )

  return jsx() as JSX.Element
}

export default Markdown
